import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  ReactiveBase,
  SingleList,
  ReactiveList,
  ReactiveComponent,
  SelectedFilters
} from '@appbaseio/reactivesearch'
import { connect } from 'react-redux'
import { buildQuery } from './helpers'
import Result from '../../components/ESSearch/Result'
import { pipe, sanitizeHTML } from '../../helpers/utils'
import { OptionsHOC, TrackingHOC } from '../../hocs'
import { useAppContext } from '../App/Context'
import languages from '../../constants/languages'
import resultProcessor from './resultProcessor'
import messages from './messages'
import ESSearch from '../../components/ESSearch'
import { getParam } from '../../helpers/parameters'
import expand from '../../assets/images/expand_more_sm.svg'
import styled from 'styled-components'
import { selectAllDevices } from '../DeviceSelector/selectors'


const List = props => {
  return (<SingleList
    {...props}
    innerClass={{
      title: 'search-title',
      input: 'search-input',
      list: 'search-list',
      radio: 'search-radio',
      count: 'search-count',
      label: 'search-label',
    }}
  />)
}
const StyledList = styled(List)`
  .search-title {
    color: var(--clr-grey-3-to-text);
  }
  .search-label {
    color: var(--clr-grey-3-to-white);
    & > span > span:nth-child(2) {
      color: var(--clr-grey-3-to-accent);
    }
  }
  `
const Results = styled.div`
  .pagination a {
    color: var(--clr-accent);
    font-weight: bold;
    background: none;
    min-height: 35px;
    border-radius: 20px;
  }
  .pagination a.active {
    color: var(--white);
    background: var(--clr-accent);
  }
  .results-info {
    justify-content: space-between;
    color: var(--clr-grey-3-to-text);
    font-size: 12px;
    span {
      font-weight: bold;
    }

    select {
      width: 100%;
      height: var(--search-input-height);
      min-width: 15ch;
      max-width: 20ch;
      border: 1px solid var(--clr-accent-to-black);
      border-radius: 0.25em;
      padding: 0.25em 0.5em;
      font-size: var(--h6);
      cursor: pointer;
      line-height: 1.1;
      background: url(${expand}) no-repeat 95% 50% var(--search-input-bg);
      outline: none;
    }
    @media (max-width: 768px) {
      margin: 2rem 0;
    }
  }
  @media (max-width: 768px) {
    --search-input-height: 40px;
}
`

const Search = ({ query, getOption, createEvent, hideSearch, devices }) => {
  if (!query) { return null }
  const { language, customer, versionKey: version } = useAppContext()
  const [reactiveSearchProps, setReactiveSearchProps] = useState({
    app: customer,
    url: null,
    headers: null,
  })
  const [showSortOptions, setShowSortOptions] = useState(true)
  const translations = messages[language]
  const landingDeviceId = getOption('landing.deviceId')
  const noresultsMsg = getOption('search.noresults', translations.noresults)
  const typeLabel = getOption('search.typeLabel', translations.type)
  const manufacLabel = getOption(
    'search.manufacturerLabel',
    translations.manufacturer
  )
  const modelLabel = getOption('search.modelLabel', translations.model)

  useEffect(() => {
    (async function() {// async iffe
      const [academyApiUrl, academyApiToken] = await Promise.all([
        getParam('academyApiUrl'),
        getParam('academyApiToken')
      ])
      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)
      setReactiveSearchProps({
        app: customer,
        url: urlParams.has('customer-override') ? `${academyApiUrl.substr(0, academyApiUrl.lastIndexOf("/"))}/v1/search` : `${academyApiUrl}/search`,
        headers: { Authorization: `Bearer ${academyApiToken}` }
      })
    })()
  }, [])


  const trackClick = url => {
    createEvent({
      category: 'search',
      data: {
        action: 'success',
        search_term: query,
        link_to: url
      }
    })

    hideSearch()
  }
  const renderResultStats = stats => {
    return (
      <div>
        <span className="bold">{`Results for ${query}`}</span>
        <p>
          {' '}
          ({stats.numberOfResults} {translations.results}){' '}
        </p>
      </div>
    )
  }

  const renderNoResults = () => {
    setShowSortOptions(false)
    return (
      <div className="no-results">
        <p>{`${noresultsMsg} "${query}"`}</p>
      </div>
    )
  }


  const langId = languages[language]

  const handleResultRender = result => {
    const item = pipe(
      resultProcessor.addUrl(language, landingDeviceId, devices),
      resultProcessor.addTitle(langId),
      resultProcessor.addDescription(langId)
    )(result)

    return (
      <Result
        item={item}
        trackClick={trackClick}
        key={item._id}
        language={language}
        getOption={getOption}
        version={version}
      />
    )
  }



  const filters = (
    <div>
      <ReactiveComponent
        componentId="CustomQuery"
        customQuery={() => buildQuery(query, langId)}
      />
      <StyledList
        componentId={typeLabel}
        dataField="phone_type"
        title={typeLabel}
        showSearch={false}
        react={{ and: ['CustomQuery', modelLabel, manufacLabel] }}
        className="filter"
      />
      <StyledList
        componentId={manufacLabel}
        dataField="manufacturer"
        title={manufacLabel}
        showSearch={false}
        react={{ and: ['CustomQuery', modelLabel, typeLabel] }}
        className="filter"
      />
      <StyledList
        componentId={modelLabel}
        dataField="model"
        title={modelLabel}
        react={{ and: ['CustomQuery', manufacLabel, typeLabel] }}
        className="filter"
        renderItem={(
          label,
          count
        ) => (
          <span>
            <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(label) }} />
            <span>{count}</span>
          </span>
        )}
      />
    </div>
  )

  const results = (
    <Results>
      <SelectedFilters clearAllLabel={translations.clearAll} />
      <ReactiveList
        componentId="SearchResult"
        dataField="name"
        pagination
        innerClass={{
          pagination: 'pagination',
          resultsInfo: 'results-info',
          sortOptions: 'sort-options hidden-xs'
        }}
        renderItem={handleResultRender}
        renderResultStats={renderResultStats}
        renderNoResults={renderNoResults}
        sortOptions={showSortOptions && [
          {
            label: translations.sortByRelevance,
            dataField: '_score',
            sortBy: 'desc'
          }
        ]}
        react={{ and: ['CustomQuery', manufacLabel, modelLabel, typeLabel] }}
      />
    </Results>
  )

  if (!reactiveSearchProps.url || !reactiveSearchProps.headers) {
    return null
  }

  return (
    <ReactiveBase {...reactiveSearchProps}>
      <ESSearch filters={filters} results={results} />
    </ReactiveBase>
  )
}

Search.defaultProps = { query: '' }

Search.propTypes = {
  query: PropTypes.string,
  getOption: PropTypes.func.isRequired,
  createEvent: PropTypes.func.isRequired,
  hideSearch: PropTypes.func.isRequired,
  version: PropTypes.string
}

const mapStateToProps = (state) => ({
  devices: selectAllDevices(state),
})

export default connect(
  mapStateToProps
)(TrackingHOC(OptionsHOC(Search)))



// WEBPACK FOOTER //
// ./src/containers/ESSearch/index.js